import React, { useState } from 'react'
import { cn } from '../../lib/utils'
import { BiChevronDown } from 'react-icons/bi'
import DropDownMenu from './DropDownMenu'
import InputFields from '../InputFields'
import { ButtonWithIcon } from './Buttons'
import { NDOrderI, PlusIcon } from '../../assets/icons/icon'
import InputF from './CommonInput'
import { t } from 'i18next'

export const InfoItem = ({ icon: Icon, label, value, iconClass, colSpan }) => (
  <div className={cn("flex items-center space-x-3", colSpan)}>
    <Icon className={cn("text-gray-400", iconClass)} />
    <div>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="text-sm text-gray-900">{value || 'N/A'}</p>
    </div>
  </div>
)


export const CollapsibleCard = ({
  icon: Icon,
  title,
  isOpen,
  onToggle,
  children,
  iconClass,
  ref
}) => {
  return (
    <div ref={ref} className="bg-[#f7f7f7] rounded-lg shadow-sm border">
      {/* Header */}
      <div
        onClick={onToggle}
        className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
      >
        <div className="flex items-center space-x-3">
          {Icon && <Icon className={cn("text-xl", iconClass)} />}
          <h3 className="text-lg font-semibold text-gray-900">
            {title}
          </h3>
        </div>

        <BiChevronDown
          size={24}
          className={cn(
            "text-gray-400 transition-transform duration-300 ease-in-out",
            {
              "rotate-180": isOpen,
            }
          )}
        />
      </div>

      {/* Body */}
      {isOpen && (
        <div className="">
          {children}
        </div>
      )}
    </div>
  )
}

const ServiceRow = ({
  label,
  waiterKey,          // "vipWaiter"
  data,               // MorningOther.vipWaiter
  updateOther,        // updateMorningOther / updateAfternoonOther
  handleNumericChange,
  isLoading,
  t,
  inputPrefix         // m | a | n
}) => {

  const isTeaCounter = waiterKey === "teaCounter";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "6px 4px",
        fontSize: "14px",
        gap: '15px'
      }}
    >

      {/* ✅ Tea Counter Special UI */}
      {isTeaCounter && (
        <>
          <TeaCounterCard
            label="Tea Counter"
            waiterKey="teaCounter"
            data={data}
            updateOther={updateOther}
            handleNumericChange={handleNumericChange}
          />
        </>
      )}

      {/* ✅ Default UI for Other Services */}
      {!isTeaCounter && (
        <>
          {/* Count */}
          <div>
            <InputFields
              name={`${inputPrefix}${waiterKey}C`}
              label={`${inputPrefix}${waiterKey}C`}
              type="text"
              placeholder={t('count')}
              isHideLabel="true"
              isFixedLabel="true"
              disabled={isLoading}
              handleChange={(e) => {
                const numericValue = handleNumericChange(e)
                updateOther(waiterKey, 'count', numericValue)
              }}
              onBlur={(e) => {
                if (!e.target.value) {
                  updateOther(waiterKey, 'price', '')
                }
              }}
              value={data.count}
              className="inputFieldClass h-10 text-center max-w-20"
            />
          </div>

          <label className="block text-xs font-medium text-gray-600 mb-1">
            {label}
          </label>
        </>
      )}
    </div>

  );
};

export const WaiterRow = ({
  label,
  waiterKey,          // "vipWaiter"
  data,               // MorningOther.vipWaiter
  updateOther,        // updateMorningOther / updateAfternoonOther
  handleNumericChange,
  isLoading,
  t,
  inputPrefix         // m | a | n
}) => {

  const isTeaCounter = waiterKey === "teaCounter";

  return (<>
    {isTeaCounter && (
      <>
        <TeaCounterCardAdvanced
          label="Tea Counter"
          waiterKey="teaCounter"
          data={data}
          updateOther={updateOther}
          handleNumericChange={handleNumericChange}
        />
      </>
    )}

    {!isTeaCounter && (
      <>
        <div className="grid grid-cols-3 gap-2">
          {/* Count */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {label}
            </label>
            <InputFields
              name={`${inputPrefix}${waiterKey}C`}
              label={`${inputPrefix}${waiterKey}C`}
              type="text"
              placeholder={t('count')}
              isHideLabel="true"
              isFixedLabel="true"
              disabled={isLoading}
              handleChange={(e) => {
                const numericValue = handleNumericChange(e)
                updateOther(waiterKey, 'count', numericValue)
              }}
              onBlur={(e) => {
                if (!e.target.value) {
                  updateOther(waiterKey, 'price', '')
                }
              }}
              value={data.count}
              className="inputFieldClass h-10"
            />
          </div>

          {/* Price */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('amount')}
            </label>
            <InputFields
              name={`${inputPrefix}${waiterKey}Price`}
              label={`${inputPrefix}${waiterKey}Price`}
              type="text"
              placeholder={t('amount')}
              isHideLabel="true"
              isFixedLabel="true"
              disabled={isLoading}
              handleChange={(e) => {
                const numericValue = handleNumericChange(e)
                updateOther(waiterKey, 'price', numericValue)
              }}
              value={data.price}
              className="inputFieldClass h-10"
            />
          </div>

          {/* Total */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('Total')}
            </label>
            <div className="w-full h-10 px-3 text-xs font-medium text-gray-900 rounded-lg bg-gray-100 border border-gray-300 flex items-center">
              {data.total || 0}
            </div>
          </div>
        </div>
      </>)}
  </>
  )
}


const OtherCharges = ({
  mealPrefix,              // "m" | "a" | "n"
  data,
  updateOther,
  handleNumericChange,
  isLoading,
  t
}) => {
  return (
    <>
      {/* Decoration / Extra / Labor */}
      <div className="grid grid-cols-6 gap-2">
        {/* Decoration Name */}
        <div className="col-span-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('decoration_name')}
          </label>
          <InputFields
            name={`${mealPrefix}DecorationName`}
            label={`${mealPrefix}DecorationName`}
            type="text"
            placeholder={t('decoration_name')}
            isHideLabel="true"
            isFixedLabel="true"
            disabled={isLoading}
            handleChange={(e) =>
              updateOther('decoration', 'name', e.target.value)
            }
            value={data.decoration.name}
            className="inputFieldClass h-10"
          />
        </div>

        {/* Decoration Amount */}
        <div className="col-span-1">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('decoration')}
          </label>
          <InputFields
            name={`${mealPrefix}DecorationAmount`}
            label={`${mealPrefix}DecorationAmount`}
            type="text"
            placeholder={t('amount')}
            isHideLabel="true"
            isFixedLabel="true"
            disabled={isLoading}
            handleChange={(e) => {
              const numericValue = handleNumericChange(e)
              updateOther('decoration', 'amount', numericValue)
            }}
            value={data.decoration.amount}
            className="inputFieldClass h-10"
          />
        </div>

        {/* Extra Amount */}
        <div className="col-span-1">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('extra')}
          </label>
          <InputFields
            name={`${mealPrefix}ExtraAmount`}
            label={`${mealPrefix}ExtraAmount`}
            type="text"
            placeholder={t('amount')}
            isHideLabel="true"
            isFixedLabel="true"
            disabled={isLoading}
            handleChange={(e) => {
              const numericValue = handleNumericChange(e)
              updateOther('extra', 'amount', numericValue)
            }}
            value={data.extra.amount}
            className="inputFieldClass h-10"
          />
        </div>

        {/* Labor Count */}
        <div className="col-span-1">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('labor')}
          </label>
          <InputFields
            name={`${mealPrefix}LaborCount`}
            label={`${mealPrefix}LaborCount`}
            type="text"
            placeholder={t('count')}
            isHideLabel="true"
            isFixedLabel="true"
            disabled={isLoading}
            handleChange={(e) => {
              const numericValue = handleNumericChange(e)
              updateOther('labor', 'count', numericValue)
            }}
            value={data.labor.count}
            className="inputFieldClass h-10"
          />
        </div>
      </div>

      {/* Extra Service */}
      <div className="grid grid-cols-2 gap-2 mt-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('Extra_Service') || 'Extra Service'}
          </label>
          <InputFields
            name={`${mealPrefix}ExtraServiceName`}
            label={`${mealPrefix}ExtraServiceName`}
            type="text"
            placeholder={t('Extra_Service_placeholder') || 'Name'}
            isHideLabel="true"
            isFixedLabel="true"
            disabled={isLoading}
            handleChange={(e) =>
              updateOther('extraService', 'name', e.target.value)
            }
            value={data.extraService.name}
            className="inputFieldClass h-10"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('Extra_Service_amount')}
          </label>
          <InputFields
            name={`${mealPrefix}ExtraServiceAmount`}
            label={`${mealPrefix}ExtraServiceAmount`}
            type="text"
            placeholder={t('Extra_Service_amount')}
            isHideLabel="true"
            isFixedLabel="true"
            disabled={isLoading}
            handleChange={(e) => {
              const numericValue = handleNumericChange(e)
              updateOther('extraService', 'amount', numericValue)
            }}
            value={data.extraService.amount}
            className="inputFieldClass h-10"
          />
        </div>
      </div>

      {/* Counter */}
      <div className="grid grid-cols-2 gap-2 mt-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('counter')}
          </label>
          <InputFields
            name={`${mealPrefix}CounterCount`}
            label={`${mealPrefix}CounterCount`}
            type="text"
            placeholder={t('count')}
            isHideLabel="true"
            isFixedLabel="true"
            disabled={isLoading}
            handleChange={(e) => {
              const numericValue = handleNumericChange(e)
              updateOther('counter', 'count', numericValue)
            }}
            value={data.counter.count || ''}
            className="inputFieldClass h-10"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('counter_detail')}
          </label>
          <textarea
            name={`${mealPrefix}CounterName`}
            placeholder={t('counter_detail')}
            disabled={isLoading}
            value={data.counter.name || ''}
            onChange={(e) =>
              updateOther('counter', 'name', e.target.value)
            }
            rows={4}
            className="w-full px-3 py-2 text-xs font-medium text-gray-900 rounded-lg bg-white border border-gray-300 focus:border-secondary focus:ring-2 focus:ring-secondary/20 transition-all duration-200 resize-none focus-within:outline-0"
          />
        </div>
      </div>
    </>
  )
}

export const MealServiceSection = ({
  mealPrefix,                // "m" | "a" | "n"
  inputValue,
  handleChange,
  calcMeal,
  mealType,
  setMealType,
  mealTypeOptions,
  OtherState,                // MorningOther / AfternoonOther / NightOther
  updateOther,               // updateMorningOther / updateAfternoonOther / updateNightOther
  handleNumericChange,
  isLoading,
  t
}) => {

  return (
    <div className="space-y-4">
      {/* Title */}
      <h4 className="font-medium text-gray-900">{t('pricing_details')}</h4>

      {/* Meal Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('Meal_Type')}
        </label>
        <DropDownMenu
          value={mealType}
          handlOnChange={(e) => setMealType(e.target.value)}
          disabled={isLoading}
          options={mealTypeOptions}
          placeholder={t('Select_Meal_Type')}
        />
      </div>

      {/* Total Person & Price */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('Total_person')}
          </label>
          <InputFields
            name={`${mealPrefix}TotalPerson`}
            type="text"
            placeholder={t('Total_person')}
            isHideLabel="true"
            isFixedLabel="true"
            disabled={isLoading}
            handleChange={(e) => {
              handleChange(e)
              calcMeal()
            }}
            value={inputValue[`${mealPrefix}TotalPerson`]}
            className="inputFieldClass px-4 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('dish_price')}
          </label>
          <InputFields
            name={`${mealPrefix}Price`}
            type="text"
            placeholder={t('dish_price')}
            isHideLabel="true"
            isFixedLabel="true"
            disabled={isLoading}
            handleChange={(e) => {
              handleChange(e)
              calcMeal()
            }}
            value={inputValue[`${mealPrefix}Price`]}
            className="inputFieldClass px-4 text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 " style={{ borderBottom: '1px solid rgb(209 213 219)' }}>
        <div className="flex items-center gap-4 text-base font-medium pb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="mode"
              value="simple"
              className="w-5 h-5 cursor-pointer"
              checked={OtherState?.mode === "default"}
              onChange={() =>
                // updateOther(prev => ({ ...prev, mode: "simple" }))
                updateOther(null, "mode", 'default')
              }
            />
            <span className="text-base">Default</span>
          </label>
        </div>

        <div className="flex items-center gap-4 text-base font-medium pb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="mode"
              value="simple"
              className="w-5 h-5 cursor-pointer"
              checked={OtherState?.mode === "simple"}
              onChange={() =>
                // updateOther(prev => ({ ...prev, mode: "simple" }))
                updateOther(null, "mode", 'simple')
              }
            />
            <span className="text-base">Simple</span>
          </label>
        </div>

        <div className="flex items-center gap-4 text-base font-medium pb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="mode"
              value="advanced"
              className="w-5 h-5 cursor-pointer"
              checked={OtherState.mode === "advanced"}
              onChange={() =>
                // updateOther(prev => ({ ...prev, mode: "advance" }))
                updateOther(null, "mode", 'advanced')
              }
            />
            <span className="text-base">Advanced</span>
          </label>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm font-medium text-gray-700">

        <div className="flex items-center gap-4 text-xs font-normal">
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 cursor-pointer"
              checked={OtherState.serviceType === "extra"}
              onChange={() => {

                updateOther(null, "serviceType", OtherState.serviceType === "extra" ? null : "extra")
                updateOther('extra', 'amount', 0)
              }
                // setOtherState(prev => ({
                //   ...prev,
                //   serviceType:
                //     prev.serviceType === "extra" ? null : "extra",
                // }))
              }
            />
            {t("is_extra")}
          </label>

          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 cursor-pointer"
              checked={OtherState.serviceType === "included"}
              onChange={() => {
                updateOther(null, "serviceType", OtherState.serviceType === "included" ? null : "included")
                updateOther('extra', 'amount', 0)
              }

                // setOtherState(prev => ({
                //   ...prev,
                //   serviceType:
                //     prev.serviceType === "included" ? null : "included",
                // }))
              }
            />
            {t("is_included")}
          </label>
        </div>
      </div>

      {OtherState?.mode === "default" && (

        <div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
            <ServiceRow
              label={t('Simple_waiter')}
              waiterKey="simpleWaiter"
              data={OtherState.simpleWaiter}
              updateOther={updateOther}
              handleNumericChange={handleNumericChange}
              isLoading={isLoading}
              t={t}
              inputPrefix={mealPrefix}
            />

            <ServiceRow
              label={t('VIP_waiter')}
              waiterKey="vipWaiter"
              data={OtherState.vipWaiter}
              updateOther={updateOther}
              handleNumericChange={handleNumericChange}
              isLoading={isLoading}
              t={t}
              inputPrefix={mealPrefix}
            />

            <ServiceRow
              label={t('PRO_waiter')}
              waiterKey="proWaiter"
              data={OtherState.proWaiter}
              updateOther={updateOther}
              handleNumericChange={handleNumericChange}
              isLoading={isLoading}
              t={t}
              inputPrefix={mealPrefix}
            />

            <ServiceRow
              label={t('PRO_couple')}
              waiterKey="proCouple"
              data={OtherState?.proCouple || 0}
              updateOther={updateOther}
              handleNumericChange={handleNumericChange}
              isLoading={isLoading}
              t={t}
              inputPrefix={mealPrefix}
            />

            <ServiceRow
              label={t('Empty_waiter')}
              waiterKey="emptyWaiter"
              data={OtherState.emptyWaiter}
              updateOther={updateOther}
              handleNumericChange={handleNumericChange}
              isLoading={isLoading}
              t={t}
              inputPrefix={mealPrefix}
            />
          </div>

          <ServiceRow
            label={t('Tea_Counter')}
            waiterKey="teaCounter"
            data={OtherState.teaCounter}
            updateOther={updateOther}
            handleNumericChange={handleNumericChange}
            isLoading={isLoading}
            t={t}
            inputPrefix={mealPrefix}
          />

          <div className="mt-3">
            <label className="block text-sm font-medium mb-1">
              {t('additional_services')}
            </label>

            <textarea
              className="w-full border rounded p-2"
              rows={3}
              value={OtherState.simpleDescription}
              onChange={e =>
                updateOther(null, "simpleDescription", e.target.value)
              }
              placeholder={t('additional_services')}
            />
          </div>

          {
            OtherState.serviceType === "extra" && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('amount')}
                </label>
                <InputFields
                  name={`${mealPrefix}ExtraAmount`}
                  label={`${mealPrefix}ExtraAmount`}
                  type="text"
                  placeholder={t('amount')}
                  isHideLabel="true"
                  isFixedLabel="true"
                  disabled={isLoading}
                  handleChange={(e) => {
                    const numericValue = handleNumericChange(e)
                    updateOther('extra', 'amount', numericValue)
                  }}
                  value={OtherState.extra.amount}
                  className="inputFieldClass h-10"
                />
              </div>
            )
          }

        </div>

      )}

      {(!OtherState?.mode || OtherState?.mode === "simple") && (<>
        <div className="mt-3">
          <label className="block text-sm font-medium mb-1">
            {t('additional_services')}
          </label>

          <textarea
            className="w-full border rounded p-2"
            rows={3}
            value={OtherState.simpleDescription}
            onChange={e =>
              updateOther(null, "simpleDescription", e.target.value)
            }
            placeholder="Enter service description..."
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('amount')}
          </label>
          <InputFields
            name='simpeAmount'
            label='simpeAmount'
            type="text"
            isHideLabel="true"
            isFixedLabel="true"
            handleChange={(e) => {
              const numericValue = handleNumericChange(e)
              updateOther(null, "simpleAmount", numericValue)
            }}
            value={OtherState?.simpleAmount}
            className="inputFieldClass h-10"
          />
        </div>

      </>)}

      {(OtherState?.mode === "advanced") && (<>
        {/* Waiters */}
        <h5 className="text-sm font-medium text-gray-700">
          {t('additional_services')}
        </h5>
        <div className="space-y-3">
          <WaiterRow
            label={t('Simple_waiter')}
            waiterKey="simpleWaiter"
            data={OtherState.simpleWaiter}
            updateOther={updateOther}
            handleNumericChange={handleNumericChange}
            isLoading={isLoading}
            t={t}
            inputPrefix={mealPrefix}
          />

          <WaiterRow
            label={t('VIP_waiter')}
            waiterKey="vipWaiter"
            data={OtherState.vipWaiter}
            updateOther={updateOther}
            handleNumericChange={handleNumericChange}
            isLoading={isLoading}
            t={t}
            inputPrefix={mealPrefix}
          />

          <WaiterRow
            label={t('PRO_waiter')}
            waiterKey="proWaiter"
            data={OtherState.proWaiter}
            updateOther={updateOther}
            handleNumericChange={handleNumericChange}
            isLoading={isLoading}
            t={t}
            inputPrefix={mealPrefix}
          />

          <WaiterRow
            label={t('PRO_couple')}
            waiterKey="proCouple"
            data={OtherState?.proCouple || 0}
            updateOther={updateOther}
            handleNumericChange={handleNumericChange}
            isLoading={isLoading}
            t={t}
            inputPrefix={mealPrefix}
          />

          <WaiterRow
            label={t('Empty_waiter')}
            waiterKey="emptyWaiter"
            data={OtherState.emptyWaiter}
            updateOther={updateOther}
            handleNumericChange={handleNumericChange}
            isLoading={isLoading}
            t={t}
            inputPrefix={mealPrefix}
          />

          <WaiterRow
            label={t('Tea_Counter')}
            waiterKey="teaCounter"
            data={OtherState.teaCounter}
            updateOther={updateOther}
            handleNumericChange={handleNumericChange}
            isLoading={isLoading}
            t={t}
            inputPrefix={mealPrefix}
          />
        </div>

        {/* Other Charges */}
        <OtherCharges
          data={OtherState}
          updateOther={updateOther}
          handleNumericChange={handleNumericChange}
          isLoading={isLoading}
          t={t}
        />
      </>)}
      {/* Total */}
      <div className="border-t pt-4 flex justify-between items-center">
        <span className="text-lg font-semibold text-gray-900">
          {t('Total')}
        </span>
        <span className="text-lg font-bold text-secondary">
          ₹{inputValue[`${mealPrefix}Total`] || 0}
        </span>
      </div>
    </div>
  )
}

export const MiscellaneousSection = ({
  titlePrefix,          // "M" | "A" | "N"
  miscList,
  setMiscList,
  unitList,
  getUnitTypes,
  t,
  lang,
  onAddClick
}) => {
  return (
    <div className="pt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-bold">
            {titlePrefix}
          </span>
          {t('Miscellaneous') || 'Miscellaneous'}
        </h4>

        <ButtonWithIcon
          text="Add"
          variant="secondary"
          btnclass="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white"
          Icon={PlusIcon}
          iconClass="w-4 h-4 mr-0"
          onClick={onAddClick}
        />
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-xl shadow-sm overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-gray-700 font-medium">
                <th className="text-left px-4 py-3 w-10">#</th>
                <th className="text-left px-4 py-3">{t('name') || 'Name'}</th>
                <th className="text-left px-4 py-3">{t('Quantity') || 'Quantity'}</th>
                <th className="text-right px-4 py-3">{t('Total') || 'Total'}</th>
                <th className="text-right px-4 py-3">{t('actions') || 'Actions'}</th>
              </tr>
            </thead>

            <tbody>
              {miscList && miscList.length > 0 ? (
                miscList.map((m, idx) => (
                  <tr
                    key={m._id}
                    className="odd:bg-white even:bg-gray-50 hover:bg-blue-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-gray-500">{idx + 1}</td>

                    <td className="px-4 py-3 font-medium text-gray-900">
                      {lang === 'gu' ? m?.name?.gu : m?.name?.en}
                    </td>

                    <td className="px-4 py-3 text-gray-700">
                      {m?.quantity} {getUnitTypes(m?.unit)} / {m?.baseQuantityFor}{' '}
                      {getUnitTypes(m?.baseQuantityUnit)}
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <input
                          type="text"
                          className="h-8 w-24 px-2 border rounded text-sm text-right"
                          value={m?.total ?? ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9.]/g, '')
                            setMiscList(prev =>
                              prev.map(x =>
                                x._id === m._id ? { ...x, total: Number(val) } : x
                              )
                            )
                          }}
                        />

                        <select
                          className="h-8 w-32 px-2 border rounded text-sm bg-white"
                          value={m?.totalUnit || ''}
                          onChange={(e) =>
                            setMiscList(prev =>
                              prev.map(x =>
                                x._id === m._id
                                  ? { ...x, totalUnit: e.target.value }
                                  : x
                              )
                            )
                          }
                        >
                          <option value="">{t('unitType') || 'Unit'}</option>
                          {unitList.map(u => (
                            <option key={u.key} value={u.key}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>

                    <td className="px-4 py-3 flex justify-end">
                      <button
                        className="flex items-center gap-1 text-xs font-medium text-red-600 border border-red-100 rounded px-2 py-1 hover:bg-red-50"
                        onClick={() =>
                          setMiscList(prev =>
                            prev.filter(x => x._id !== m._id)
                          )
                        }
                      >
                        ✕ Remove
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="px-4 py-8 text-center text-gray-500 text-sm"
                  >
                    {t('no_items_selected') || 'No items selected'}
                  </td>
                </tr>
              )}
            </tbody>

            {miscList && miscList.length > 0 && (
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td
                    colSpan="5"
                    className="px-4 py-2 text-xs text-gray-600 text-right font-medium"
                  >
                    {miscList.length} {t('items') || 'items'}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  )
}
export const DataCard = ({ className, Icon, title, desc, titleClass, descClass, iconClass, isInput, children }) => {
  return (
    <div className={cn("rounded-lg p-4 border ", className)}>
      <div className={`F_JC_IC ${isInput && 'gap-1'}`}>
        <div>
          <p className={cn("text-sm font-medium", titleClass)}>{title}</p>
          {isInput ? (children) : (<p className={cn("text-2xl font-bold", descClass)}>{desc}</p>)}

        </div>
        <div className={cn("w-10 h-10 rounded-lg flex justify-center items-center", iconClass)}>
          {Icon && <Icon className={cn("")} />}
        </div>
      </div>
    </div>
  )
}
export const NoMenuItem = () => {
  return (
    <div className="min-h-[200px] bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl border border-gray-100 flex justify-center items-center">
      <div className="text-center py-8 px-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-white/80 backdrop-blur-sm rounded-full flex justify-center items-center shadow-sm">
          <NDOrderI className='w-8 h-8 text-indigo-500 mb-0' />
        </div>
        <h3 className="text-lg font-medium text-gray-800 mb-2">{t('no_menu_items_selected')}</h3>
        <p className="text-gray-600 text-sm max-w-sm mx-auto">
          {t('no_menu_items_message')}
        </p>
      </div>
    </div>
  )
}
export const TeaCounterCard = ({
  label = "Tea Counter",
  waiterKey = "teaCounter",
  data,
  updateOther,
  handleNumericChange
}) => {

  return (
    <div
      className={`rounded-xl border w-full p-5 transition-all duration-300 shadow-sm bg-whiteborder-gray-200`}
    >
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b">

        <h3
          className={`text-base font-semibold transition text-gray-800`}
        >
          {t('Tea_Counter')}
        </h3>
      </div>

      {/* Expand Section */}
      <div
        className={`transition-all duration-300 overflow-hidden max-h-96 opacity-100 pt-4`}
      >
        <div className="space-y-4">

          {/* Duration */}
          <div className="grid grid-cols-[120px_1fr] items-center">
            <span className="text-sm text-gray-500">
              {t('Duration')}
            </span>

            <div className="inline-flex rounded-lg border bg-gray-100 p-1 w-full">
              <button
                className={`px-4 py-1 text-sm rounded-md transition w-full ${data?.duration === "full"
                  ? "bg-blue-200 text-blue-700 shadow-sm"
                  : "text-gray-500"
                  }`}
                onClick={() =>
                  updateOther(waiterKey, "duration", "full")
                }
              >
                {t('Full_Day')}
              </button>

              <button
                className={`px-4 py-1 text-sm rounded-md transition w-full ${data?.duration === "half"
                  ? "bg-blue-200 text-blue-700 shadow-sm"
                  : "text-gray-500"
                  }`}
                onClick={() =>
                  updateOther(waiterKey, "duration", "half")
                }
              >
                {t('Bapor_To_Ratre')}
              </button>
            </div>
          </div>

          {/* Counter */}
          <div className="grid grid-cols-[120px_auto] items-center">
            <span className="text-sm text-gray-500">
              {t('Counters')}
            </span>

            <input
              type="number"
              value={data?.count || ""}
              onChange={(e) => {
                const numericValue = handleNumericChange
                  ? handleNumericChange(e)
                  : e.target.value;

                updateOther(waiterKey, "count", numericValue);
              }}
              className="w-full text-center rounded-md border border-gray-300 h-9 focus:outline-none"
            />
          </div>

        </div>
      </div>
    </div>
  );
};

const TeaCounterCardAdvanced = ({
  waiterKey,
  data,
  updateOther,
  handleNumericChange
}) => {

  const total =
    Number(data?.count || 0) * Number(data?.price || 0);

  return (
    <div
      className={`rounded-xl border p-5 transition-all duration-300 shadow-sm bg-gray-50 border-gray-200`}
    >
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b">
        <h3
          className={`text-base font-semibold transition text-gray-700`}
        >
          {t("Tea_Counter")}
        </h3>
      </div>

      {/* Body */}
      <div className="pt-4 space-y-4">

        {/* Duration */}
        <div className="grid grid-cols-[120px_1fr] items-center">
          <span className="text-sm text-gray-500">
            {t("Duration")}
          </span>

          <div className="inline-flex rounded-lg border bg-gray-100 p-1 w-full">
            <button
              className={`px-4 py-1 text-sm rounded-md transition w-full ${data?.duration === "full"
                ? "bg-blue-200 text-blue-700 shadow-sm"
                : "text-gray-500"
                }`}
              onClick={() =>
                updateOther(waiterKey, "duration", "full")
              }
            >
              {t("Full_Day")}
            </button>

            <button
              className={`px-4 py-1 text-sm rounded-md transition w-full ${data?.duration === "bapor"
                ? "bg-blue-200 text-blue-700 shadow-sm"
                : "text-gray-500"
                }`}
              onClick={() =>
                updateOther(waiterKey, "type", "half")
              }
            >
              {t("Bapor_To_Ratre")}
            </button>
          </div>
        </div>

        {/* Count */}
        <div className="grid grid-cols-3 gap-2">
          <div className="">
            <span className="text-sm text-gray-500">
              {t("Counters")}
            </span>

            <input
              type="number"
              value={data?.count || ""}
              onChange={(e) =>
                updateOther(
                  waiterKey,
                  "count",
                  handleNumericChange(e)
                )
              }
              className="w-full text-center rounded-md border border-gray-300 h-9 focus:outline-none"
            />
          </div>

          {/* price */}
          <div className="">
            <span className="text-sm text-gray-500">
              {t("amount")}
            </span>

            <input
              type="number"
              value={data?.price || ""}
              onChange={(e) =>
                updateOther(
                  waiterKey,
                  "price",
                  handleNumericChange(e)
                )
              }
              className="w-full text-center rounded-md border border-gray-300 h-9 focus:outline-none"
            />
          </div>

          {/* Total (Auto Calculated) */}
          <div className="">
            <span className="text-sm text-gray-500">
              {t("Total")}
            </span>

            <div className="h-9 flex items-center justify-center rounded-md bg-gray-100 font-medium">
              {total}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};